import { isAxiosError } from 'axios';
import { createLogger } from 'logging';

import { CreditTransactionRecord } from '@videoblocks/subscription-service-client';

import { BadRequestError } from './BadRequestError';
import { ProcessingError } from './ProcessingError';
import { UnexpectedError } from './UnexpectedError';
import { creditApi } from './subscriptionServiceConfig';

const logger = createLogger('Credit Api');

const getBalance = async (accountCode: string, userId?: number): Promise<number> => {
    const params = { accountCode, userId };
    try {
        const response = await creditApi.getBalance(params);
        return response.data.balance ?? 0;
    } catch (error) {
        if (isAxiosError(error)) {
            if (error.response?.status === 400) {
                logger.warn(
                    `Bad request error while fetching account balance: ${JSON.stringify({ error, ...params })}`,
                );
                throw new BadRequestError();
            } else if ((error.response?.status || 0) >= 500) {
                logger.warn(`Processing error while fetching account balance: ${JSON.stringify({ error, ...params })}`);
                throw new ProcessingError(
                    'There was an error while fetching your account balance.\n Please try again later.',
                );
            }
        }
        logger.error(
            `An unexpected error occurred while fetching account balance: ${JSON.stringify({ error, ...params })}`,
        );
        throw new UnexpectedError();
    }
};

const getTransactionHistory = async (accountCode: string, userId?: number): Promise<CreditTransactionRecord[]> => {
    const params = { accountCode, userId };
    try {
        const response = await creditApi.getTransactionHistory(params);
        return response.data;
    } catch (error) {
        if (isAxiosError(error)) {
            if (error.response?.status === 400) {
                logger.warn(
                    `Bad request error while fetching transaction history: ${JSON.stringify({ error, ...params })}`,
                );
                throw new BadRequestError();
            } else if ((error.response?.status || 0) >= 500) {
                logger.warn(
                    `Processing error while fetching transaction history: ${JSON.stringify({ error, ...params })}`,
                );
                throw new ProcessingError(
                    'There was an error while fetching your transaction history.\n Please try again later.',
                );
            }
        }
        logger.error(
            `An unexpected error occurred while fetching transaction history: ${JSON.stringify({ error, ...params })}`,
        );
        throw new UnexpectedError();
    }
};

const postTransaction = async (
    accountCode: string,
    creditTransactionRecord: Omit<CreditTransactionRecord, 'accountCode'>,
): Promise<number> => {
    const params = {
        accountCode,
        creditTransactionRecord: { ...creditTransactionRecord, accountCode },
    };
    try {
        const response = await creditApi.postTransaction(params);
        return response.data.balance;
    } catch (error) {
        if (isAxiosError(error)) {
            if (error.response?.status === 400) {
                logger.warn(
                    `Bad request error while posting credit transaction: ${JSON.stringify({ error, ...params })}`,
                );
                throw new BadRequestError();
            } else if ((error.response?.status || 0) >= 500) {
                logger.warn(
                    `Processing error while posting credit transaction: ${JSON.stringify({ error, ...params })}`,
                );
                throw new ProcessingError(
                    'There was an error while processing your transaction.\n Please try again later.',
                );
            }
        }
        logger.error(
            `An unexpected error occurred while posting credit transaction: ${JSON.stringify({ error, ...params })}`,
        );
        throw new UnexpectedError();
    }
};

export const CreditApi = {
    getBalance,
    getTransactionHistory,
    postTransaction,
};
